/**
 * Barwy dokumentu „VoltValue Projekt" wyliczane z JEDNEGO koloru firmy.
 *
 * ═══ DLACZEGO JEDEN KOLOR, A NIE PALETA ═══
 * Firma ustawia w koncie wyłącznie kolor podstawowy. Ciemny pas nagłówka, jasne
 * tło kafelków i kolor obramowań powstają z niego tutaj. Gdyby każdy z tych
 * odcieni był osobnym ustawieniem, dokument rozjeżdżałby się kolorystycznie
 * przy pierwszej nieuważnej zmianie — a i tak nikt nie chce dobierać czterech
 * barw, żeby wysłać ofertę.
 *
 * ═══ CZYTELNOŚĆ PILNUJE BACKEND ═══
 * Kolor zapisany w ustawieniach ma już zagwarantowany kontrast 4,5:1 z bielą
 * (accounts/api.py, `_normalizuj_kolor`). Tutaj go NIE sprawdzamy ponownie:
 * dwa miejsca liczące to samo prędzej czy później zaczną się różnić, a wtedy
 * nie wiadomo, które ma rację. Ciemny wariant jest z definicji ciemniejszy od
 * podstawowego, więc dziedziczy tę gwarancję.
 */

/** `#RRGGBB` albo skrót `#RGB` na `[r, g, b]`. `null`, gdy zapis jest zły. */
export const naRgb = (hex) => {
  if (typeof hex !== 'string') return null
  let zapis = hex.trim().replace(/^#/, '')
  if (/^[0-9a-f]{3}$/i.test(zapis)) zapis = zapis.split('').map((c) => c + c).join('')
  if (!/^[0-9a-f]{6}$/i.test(zapis)) return null
  return [0, 2, 4].map((i) => parseInt(zapis.slice(i, i + 2), 16))
}

const naHex = (rgb) => '#' + rgb.map((k) => {
  const c = Math.max(0, Math.min(255, Math.round(k)))
  return c.toString(16).padStart(2, '0')
}).join('').toUpperCase()

/**
 * Mieszanie dwóch barw w przestrzeni sRGB.
 *
 * Świadomie BEZ korekty gamma. Wynik wychodzi odrobinę ciemniejszy niż przy
 * mieszaniu liniowym, ale zgadza się z tym, co robi CSS przy `color-mix` w sRGB
 * i z tym, czego spodziewa się osoba dobierająca kolor w programie graficznym.
 * Zgodność z narzędziami, których używa firma, jest tu warta więcej niż
 * teoretyczna poprawność.
 */
const mieszaj = (a, b, udzialB) => a.map((k, i) => k + (b[i] - k) * udzialB)

const CZERN = [0, 0, 0]
const BIEL = [255, 255, 255]

/** Ciemny tekst dokumentu — musi zgadzać się z `LUMINANCJA_CIEMNEGO_TEKSTU` w API. */
const CIEMNY_TEKST = '#111827'

const kanalLiniowy = (skladowa) => {
  const s = skladowa / 255
  return s <= 0.04045 ? s / 12.92 : ((s + 0.055) / 1.055) ** 2.4
}

const luminancja = (rgb) => {
  const [r, g, b] = rgb.map(kanalLiniowy)
  return 0.2126 * r + 0.7152 * g + 0.0722 * b
}

/**
 * Kolor napisu dobrany do tła: biel albo ciemny grafit.
 *
 * ═══ TO JEST POWÓD, DLA KTÓREGO ŻÓŁTY MOŻE BYĆ ŻÓŁTY ═══
 * Wymuszanie białego tekstu znaczyło, że tło musi mieć luminancję poniżej 0,18.
 * Żadna żółć ani pomarańcz się w tym nie mieszczą — po przyciemnieniu do progu
 * zamieniają się w ochrę i brąz. Firma prosząca o żółty dostawała brązowy.
 *
 * Odwracamy zależność: tło zostaje takie, jakie wybrała firma, a dopasowuje się
 * NAPIS. Dla `#FACC15` ciemny tekst daje kontrast 11,6, dla `#2563EB` biel 5,2.
 */
export const tekstNaTle = (rgb) => {
  const lum = luminancja(rgb)
  const zBiela = 1.05 / (lum + 0.05)
  const zCiemnym = (lum + 0.05) / (luminancja(naRgb(CIEMNY_TEKST)) + 0.05)
  return zBiela >= zCiemnym ? '#FFFFFF' : CIEMNY_TEKST
}

/**
 * Komplet barw dokumentu.
 *
 * `podstawowy` — tło pełnowymiarowych pasów i akcenty (kreski, numery, ptaszki)
 * `naPasku`    — kolor napisu NA tym tle, dobrany automatycznie
 * `jasny`      — tło kafelków i banerów
 * `obwodka`    — obramowania kafelków; sam jasny ich nie odcina
 * `ciemny`     — kolor TEKSTU na białym i na jasnym tle; nigdy nie jest tłem
 */
export const barwyDokumentu = (kolor) => {
  const rgb = naRgb(kolor) || naRgb(KOLOR_DOMYSLNY)

  // `ciemny` służy WYŁĄCZNIE do pisania na jasnym tle, więc musi być ciemny
  // niezależnie od tego, jak jasny jest kolor firmy. Dla żółci wychodzi z tego
  // ochra — i tak ma być: to kolor drobnego napisu, a nie plamy na okładce.
  const doTekstu = luminancja(rgb) > 0.25 ? mieszaj(rgb, CZERN, 0.55) : mieszaj(rgb, CZERN, 0.25)

  return {
    podstawowy: naHex(rgb),
    naPasku: tekstNaTle(rgb),
    ciemny: naHex(doTekstu),
    jasny: naHex(mieszaj(rgb, BIEL, 0.92)),
    obwodka: naHex(mieszaj(rgb, BIEL, 0.78)),
  }
}

/** Kolor platformy — musi zgadzać się z `DOMYSLNY_KOLOR_DOKUMENTU` w backendzie. */
export const KOLOR_DOMYSLNY = '#2563EB'

/**
 * Zmienne CSS do wstawienia w `style` korzenia dokumentu.
 *
 * Zmienne, a nie klasy Tailwinda: kolor jest znany dopiero w czasie działania,
 * a Tailwind buduje klasy przy kompilacji. Klasy typu `bg-[var(--vv-dok)]`
 * działają i pozwalają zostać przy jednym sposobie stylowania w całym projekcie.
 */
export const zmienneKoloru = (kolor) => {
  const b = barwyDokumentu(kolor)
  return {
    '--vv-dok': b.podstawowy,
    '--vv-dok-na-pasku': b.naPasku,
    '--vv-dok-ciemny': b.ciemny,
    '--vv-dok-jasny': b.jasny,
    '--vv-dok-obwodka': b.obwodka,
  }
}

/**
 * Gotowe odcienie do wyboru w ustawieniach.
 *
 * Wszystkie przechodzą próg kontrastu bez korekty, więc kliknięcie
 * któregokolwiek nigdy nie kończy się komunikatem „przyciemniliśmy twój kolor".
 *
 * Żółć i pomarańcz są PRAWDZIWE, a nie przyciemnione do ochry — pozwala na to
 * dobieranie koloru napisu do tła (`tekstNaTle`). Na `#FACC15` staje ciemny
 * grafit i daje kontrast 11,6; na granacie biel i 5,2. Przy poprzednim podejściu
 * z wymuszoną bielą każda żółć musiała zejść do brązu.
 */
export const PALETA = [
  { hex: '#2563EB', nazwa: 'Niebieski VoltValue' },
  { hex: '#0F5B70', nazwa: 'Morski' },
  { hex: '#15803D', nazwa: 'Zielony' },
  { hex: '#1E293B', nazwa: 'Grafitowy' },
  { hex: '#9F1239', nazwa: 'Bordowy' },
  { hex: '#6D28D9', nazwa: 'Fioletowy' },
  { hex: '#FACC15', nazwa: 'Żółty' },
  { hex: '#F59E0B', nazwa: 'Bursztynowy' },
  { hex: '#F97316', nazwa: 'Pomarańczowy' },
  { hex: '#C2410C', nazwa: 'Ceglasty' },
]
